import md5 from "blueimp-md5";
import Environment from "../util/Environment";
import curry from "../util/curry";
import EventTarget, { EventHandler } from "../util/EventTarget";
import { logError } from "../util/monitoring/clientUtilsNR";
import fireEREvt from "../util/eventRecorderClient";
import { getConsentFlags } from "../util/consent";
import Viewability, { AdSizes, type AdSizeMap } from "../viewability/Viewability";
import { Targeting } from "../interfaces/TargetingProvider";
import CompanionGroup from "../refreshing/CompanionGroup";
import SlotRenderEndedEvent from "./SlotRenderEndedEvent";
import fixInlineBlockBaseline from "./fixInlineBlockBaseline";

const AD_ELEMENT_CLASS = "bb-ads__ad";
const RENDERED_CLASS = "bb-ads__rendered";
const EMPTY_CLASS = "bb-ads__empty";
const AD_ELEMENT_NO_SIZE_CLASS = "bb-ads__no_size";
const AD_CREATED = "sparkleAdCreated";

const isEmpty = ({ isEmpty: assertEmpty }: { isEmpty: boolean }) => assertEmpty;

const isOneByOne = ({ size = [] }: googletag.events.SlotRenderEndedEvent) => {
    // @ts-expect-error ts migration
    const [first, second] = size;

    return (first === 1 && second === 1);
};

export default class Ad extends EventTarget {

    private _refreshAttemptCounter = 0;
    private _refreshCount = 0;
    private _unitName: string;
    private _dimensions: AdSizeMap;
    private _targeting: Targeting;
    private _slot: googletag.Slot | null;
    private _viewportOffset = 0;
    private _renderTime: DOMHighResTimeStamp | null = null;
    private _slotRenderedEvent: SlotRenderEndedEvent | null = null;
    private _isInterstitial = false;
    private _triggeredRenderedEventArgument: SlotRenderEndedEvent | null = null;
    private _triggeredEmptyEventArgument: SlotRenderEndedEvent | null = null;
    private _containerId: string;
    private _viewability: Viewability;
    private _isDecoupled: boolean;
    private _isMobile = false;
    private _sizes: AdSizes;
    private _id: string = "";
    private _requestTime: DOMHighResTimeStamp | undefined = undefined;
    private _companionGroup: CompanionGroup | undefined = undefined;
    private _shadowHostElement: HTMLElement | undefined = undefined;

    static EVENTS = {
        ON_RENDER: "render",
        ON_BEFORE_RENDER: "beforeRender",
        ON_EMPTY: "empty",
        ON_LOAD: "load"
    };

    constructor(
        viewability: Viewability,
        containerId: string,
        unitName: string,
        dimensions: AdSizeMap,
        targeting: Targeting = {},
        isDecoupled = false,
        shadowHostElement?: HTMLElement
    ) {
        super();

        if (targeting.tickers) {
            targeting.tickers = JSON.stringify(targeting.tickers);
        }

        this._unitName = unitName;
        this._dimensions = dimensions;
        this._targeting = targeting;
        this._slot = null;
        this._viewportOffset = 0;
        this._renderTime = null;
        this._slotRenderedEvent = null;
        this._isInterstitial = false;
        this._triggeredRenderedEventArgument = null;
        this._triggeredEmptyEventArgument = null;
        this._containerId = containerId;
        this._viewability = viewability;
        this._isDecoupled = isDecoupled;
        this._shadowHostElement = shadowHostElement;

        this._isMobile = false;
        this._sizes = this.getSizes();
        this._contextifyCallbacks();
        this._setId();
        this._renderContainer(containerId);

        this.onRender(curry(this.setClassName, RENDERED_CLASS));
        this.onEmpty(curry(this.setClassName, EMPTY_CLASS));
    }

    /**
     * Bind methods to self for use as callbacks
     *
     * @private
     */
    _contextifyCallbacks() {
        this.setClassName = this.setClassName.bind(this);
    }

    /**
     * Computes unique id from sizes, targeting, and unit name
     *
     * @private
     */
    _setId() {
        const idComponents = [
            this._dimensions,
            this._targeting,
            this._unitName
        ];

        this._id = md5(JSON.stringify(idComponents));
    }

    /**
     * Creates and prepends ad element to self
     *
     * @private
     */
    _renderContainer(containerId: string) {
        let container = this._shadowHostElement
            ? this._shadowHostElement.shadowRoot?.getElementById(containerId) as HTMLElement | null
            : document.getElementById(containerId);

        if (!container) {
            // eslint-disable-next-line no-console
            console.log(`Failed to render: Ad container did not exist for id ${ containerId }`);

            return;
        }

        let adElement: HTMLElement | null = document.createElement("div");

        adElement.setAttribute("class", AD_ELEMENT_CLASS);
        adElement.setAttribute("id", this._id);
        adElement.setAttribute("aria-hidden", "true");
        container.insertBefore(adElement, container.firstChild);
        container = null;
        adElement = null;
    }

    /**
     * @returns {boolean}
     */
    isDecoupled() {
        return this._isDecoupled;
    }

    getDomId(): string | null {
        return this._slot ? this._slot.getSlotElementId() : null;
    }

    /**
     * @returns {string}
     */
    getId() {
        return this._id;
    }

    /**
     * Counts refresh attempts, including ones a refresh strategy later
     * filters out. Only used to name performance metrics.
     *
     * @returns {number}
     */
    getAndIncrementRefreshAttempt() {
        this._refreshAttemptCounter++;

        return this._refreshAttemptCounter;
    }

    /**
     * Counts refreshes actually issued to GPT, unlike
     * {@link getAndIncrementRefreshAttempt}
     *
     * @returns {number}
     */
    incrementRefreshCount() {
        this._refreshCount++;

        return this._refreshCount;
    }

    /**
     * @returns {number}
     */
    getRefreshCount() {
        return this._refreshCount;
    }

    getSizes() {
        if (!this._sizes || !this._sizes?.length) {
            this._sizes = this._viewability.getSizes(this._dimensions);
        }

        return this._sizes;
    }

    /**
     * @returns {boolean}
     */
    isDisplayable() {
        return Boolean(this.getSizes().length);
    }

    getSlot() {
        return this._slot;
    }

    getTargeting() {
        return Object.assign({}, this._targeting, {
            pmid: this.getId()
        });
    }

    addTargeting(newTargeting: Targeting) {
        Object.assign(this._targeting, newTargeting);
    }

    getElement(): HTMLElement | null {
        if (this._shadowHostElement) {
            return this._shadowHostElement.shadowRoot?.getElementById(this._id) as HTMLElement | null;
        }

        return document.getElementById(this._id);
    }

    getElementTopPosition() {
        const el = this.getElement();

        if (!document || !el) {
            return Infinity;
        }

        return el.getBoundingClientRect().top + document.documentElement.scrollTop;
    }

    getIframe() {
        const element = this.getElement();
        const iframes = element ? Array.from(element.getElementsByTagName("iframe")) : [];
        const foundIframe = iframes.find((f) => Boolean(f.offsetParent)) || null;

        if (foundIframe && !foundIframe.hasAttribute("title")) {
            foundIframe.setAttribute("title", "Advertisement content");
        }

        return foundIframe;
    }

    getUnitName() {
        return this._unitName;
    }

    getPerformanceId() {
        return this._targeting.performanceId;
    }

    hasSlot() {
        return Boolean(this._slot);
    }

    setHeight(height: string) {
        let iframe = this.getIframe();

        if (iframe) {
            iframe.setAttribute("height", height);
            iframe = null;
        }

        return this;
    }

    setDimensions(width: string, height: string) {
        let iframe = this.getIframe();

        if (iframe) {
            iframe.setAttribute("width", width);
            iframe.setAttribute("height", height);
            iframe = null;
        }

        return this;
    }

    setClassName(className: string) {
        const element = this.getElement();

        if (element) {
            element.classList.add(className);

            if (this._sizes.some((size) => Boolean(size[0]) && size[0] === size[1])) {
                element.classList.add(AD_ELEMENT_NO_SIZE_CLASS);
                this._sizes.forEach((size) => {
                    if (Boolean(size[0]) && size[0] === size[1]) {
                        element.classList.add(`bb-ads__size_${ size[0] }x${ size[1] }`);
                    }
                });
            }
        }

        return this;
    }

    /**
     * Sets slot
     *
     * @param {googletag.Slot} slot
     * @returns {Ad}
     */
    setSlot(slot: googletag.Slot) {
        this._slot = slot;

        return this;
    }

    /**
     * Sets the viewport offset
     *
     * @param {number} viewportOffset
     * @returns {Ad}
     */
    setViewportOffset(viewportOffset: number) {
        this._viewportOffset = viewportOffset;

        return this;
    }

    /**
     * Get viewport offset
     *
     * @returns {number}
     */
    getViewportOffset() {
        return this._viewportOffset;
    }

    setRequestTime(requestTime: DOMHighResTimeStamp) {
        this._requestTime = requestTime;
    }

    /**
     * Get GPT request start time
     *
     * @returns {DOMHighResTimeStamp,}
     */
    getRequestTime() {
        return this._requestTime;
    }

    getRenderTime(): DOMHighResTimeStamp | null {
        return this._renderTime;
    }

    /**
     * Get SlotRenderEndedEvent
     */
    setSlotRenderedEvent(event: SlotRenderEndedEvent): void {
        this._slotRenderedEvent = event;
    }

    getSlotRenderedEvent(): SlotRenderEndedEvent | null {
        return this._slotRenderedEvent;
    }

    /**
     * Executes callback when the ad has been rendered and is not empty
     */
    onRender(callback: EventHandler<SlotRenderEndedEvent>): Ad {
        // There is a chance that render finish before onRender is executed.
        // Therefore we need to distinguish between rendered and empty flags
        if (this._triggeredRenderedEventArgument) {
            callback(this._triggeredRenderedEventArgument);

            return this;
        }

        this.addEventListener(Ad.EVENTS.ON_RENDER, callback);

        return this;
    }

    /**
     * Executes callback when the ad was served with an empty response. Empty
     * refers to a lack of inventory for ads targeting
     */
    onEmpty(callback: EventHandler<SlotRenderEndedEvent>): Ad {
        // There is a chance that render finish before onEmpty is executed.
        // Therefore we need to distinguish between rendered and empty flags
        if (this._triggeredEmptyEventArgument) {
            callback(this._triggeredEmptyEventArgument);

            return this;
        }

        this.addEventListener(Ad.EVENTS.ON_EMPTY, callback);

        return this;
    }

    /**
     * Delegates a rendering event to the appropriate event listeners based on
     * the event being empty or not
     */
    handleTaglessRenderEvent(event: SlotRenderEndedEvent): void {
        this._triggeredRenderedEventArgument = event;
        this._slotRenderedEvent = event;
        const element = this.getElement();

        if (element) {
            element.removeAttribute("aria-hidden");
        }
        this.dispatchEvent(Ad.EVENTS.ON_RENDER, event);
    }

    /**
     * Delegates a rendering event to the appropriate event listeners based on
     * the event being empty or not
     */
    handleSlotRenderEvent(event: googletag.events.SlotRenderEndedEvent): void {
        const useEmptyCallbacks = isEmpty(event) || isOneByOne(event);

        this._renderTime = Environment.currentTime();

        this._slotRenderedEvent = new SlotRenderEndedEvent({
            id: this.getId(),
            adCode: this.getUnitName(),
            requestTime: this.getRequestTime(),
            event
        });

        const element = this.getElement();

        if (useEmptyCallbacks) {
            if (element) {
                element.setAttribute("aria-hidden", "true");
            }
            this.dispatchEvent(Ad.EVENTS.ON_EMPTY, this._slotRenderedEvent);
            this.removeEventListener(Ad.EVENTS.ON_RENDER);
            this._triggeredEmptyEventArgument = this._slotRenderedEvent;
        } else {
            if (element) {
                element.removeAttribute("aria-hidden");
            }
            this.dispatchEvent(Ad.EVENTS.ON_RENDER, this._slotRenderedEvent);
            this.removeEventListener(Ad.EVENTS.ON_EMPTY);
            this._triggeredRenderedEventArgument = this._slotRenderedEvent;
        }

        const { creativeId, lineItemId, campaignId, size, advertiserId } = event;
        const { reqAllowed, funAllowed, padAllowed } = getConsentFlags();

        fireEREvt("ad-render", {
            "container-id": this._containerId,
            creativeId,
            lineItemId,
            campaignId,
            size,
            advertiserId,
            isEmpty: useEmptyCallbacks,
            reqAllowed,
            funAllowed,
            padAllowed,
            refreshCount: this._refreshCount
        });
    }

    handleSlotOnLoadEvent(event?: googletag.events.SlotOnloadEvent, taglessIframe?: HTMLIFrameElement) {
        this.dispatchEvent(Ad.EVENTS.ON_LOAD, event);
        fireEREvt("ad-load", { "container-id": this._containerId });
        // check for iframe
        const iframe = taglessIframe || this.getIframe();

        if (iframe) {
            // ADSTEAM-2554/2614: neutralize the inline-block baseline gap for
            // fixed-height creatives (300x250, 320x50)
            fixInlineBlockBaseline(iframe);

            iframe.addEventListener("load", () => {
                fireEREvt("ad-iframe-loaded", { "container-id": this._containerId });
                // re-apply in case the friendly iframe (re)navigated on load
                fixInlineBlockBaseline(iframe);
            });
            // This "data-load-complete" attribute won't be found in our tagless iframe
            // since it is a gpt custom attribute
            const dataLoaded = iframe.getAttributeNode("data-load-complete");

            if (dataLoaded) {
                if (dataLoaded.textContent === "true") {
                    fireEREvt("ad-iframe-dataloaded", { "container-id": this._containerId });
                }
            }
        }
    }

    /**
     * Cleans up ad container for new slot
     */
    handleSlotResponseReceivedEvent(): void {
        this._resetContainer();
        fireEREvt("ad-resp-recv", { "container-id": this._containerId });
    }

    getContainerId(): string {
        return this._containerId;
    }

    getShadowHostElement(): HTMLElement | undefined {
        return this._shadowHostElement;
    }

    _resetContainer(): void {
        const container = document.getElementById(this._containerId);

        if (!container) {
            logError(new Error(`Failed to reset container: Ad container did not exist for id ${ this._containerId }`));

            return;
        }

        container.setAttribute("style", "");

        const adElement = container.getElementsByClassName(AD_ELEMENT_CLASS).item(0);

        if (!adElement) {
            logError(new Error(`Failed to reset container: Ad element does not exist for the following container: ${ this._containerId }`));

            return;
        }

        adElement.setAttribute("style", "");
    }

    /**
     * Sets a flag which designates ad for out of page slot
     */
    setInterstitialFlag(flag: boolean) {
        this._isInterstitial = flag;

        return this;
    }

    isInterstitial(): boolean {
        return this._isInterstitial;
    }

    setCompanionGroup(companionGroup: CompanionGroup): void {
        this._companionGroup = companionGroup;
    }

    companionGroup(): CompanionGroup | null {
        return this._companionGroup || null;
    }

    notifyAdCreated(): Ad {
        const containerId = this.getContainerId();

        window.dispatchEvent(new CustomEvent(AD_CREATED, {
            detail: { containerId }
        }));

        return this;
    }

    isCompanionChild(): boolean {
        // @ts-expect-error ts migration
        return (this.companionGroup() && this.companionGroup().children()
            .lastIndexOf(this) >= 0);
    }

    isMobile(): boolean {
        this._isMobile = this._viewability?.isMobile(this._dimensions);

        return this._isMobile;
    }

    destroy(): void {
        // @ts-expect-error ts migration
        this._unitName = null;
        // @ts-expect-error ts migration
        this._sizes = null;
        // @ts-expect-error ts migration
        this._targeting = null;
        this._slot = null;
        this._slotRenderedEvent = null;
    }

}
